/**
 * Finding card — single review finding: a `High · Clarity` meta line, the
 * message, the excerpt quote, and one row of actions.
 *
 * The action row is all borderless so it fits the AI panel's default width
 * without clipping: Fix with AI (`ai-ghost` — repeated on every card, a filled
 * button would stop meaning "the main action"), Go to section, Mark fixed, and
 * a `⋯` menu holding the rarer Add as comment.
 */

import { useLayoutEffect, useRef, useState } from 'react'
import {
  ArrowRight,
  Wand2,
  AlertTriangle,
  X,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Check,
  MoreHorizontal,
  Undo2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { REVIEW_SEVERITY_LABEL, type MergedReviewFinding } from '@/lib/schemas'
import { excerptMatches, flashSection } from '@/lib/editor-bridge'
import { useReviewStore } from '@/stores/review-store'

export interface FindingCardProps {
  artifactId: string
  finding: MergedReviewFinding
  canFix: boolean
  onFix?: ((finding: MergedReviewFinding) => void) | undefined
  canPromoteToComment?: boolean | undefined
  onPromoteToComment?: ((finding: MergedReviewFinding) => void) | undefined
  alreadyPromoted?: boolean | undefined
}

export function FindingCard({
  artifactId,
  finding,
  canFix,
  onFix,
  canPromoteToComment,
  onPromoteToComment,
  alreadyPromoted,
}: FindingCardProps) {
  const dismissFinding = useReviewStore((s) => s.dismissFinding)
  const markFindingFixed = useReviewStore((s) => s.markFindingFixed)
  const [outdated, setOutdated] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const messageRef = useRef<HTMLParagraphElement | null>(null)
  const excerptRef = useRef<HTMLQuoteElement | null>(null)

  // Measure clamped content against its natural size. If either block
  // overflows when collapsed, surface the "Show more" toggle.
  useLayoutEffect(() => {
    if (expanded) return
    const m = messageRef.current
    const e = excerptRef.current
    const overflowed =
      (m != null && m.scrollHeight - m.clientHeight > 1) ||
      (e != null && e.scrollHeight - e.clientHeight > 1)
    setHasOverflow(overflowed)
  }, [expanded, finding.message, finding.excerpt])

  const severityDot =
    finding.severity === 'high'
      ? 'bg-destructive'
      : finding.severity === 'medium'
        ? 'bg-[hsl(var(--status-warning))]'
        : 'bg-muted-foreground'
  const severityLabel = REVIEW_SEVERITY_LABEL[finding.severity]

  const handleJump = () => {
    if (!finding.sectionId) return
    if (finding.excerpt && !excerptMatches(finding.sectionId, finding.excerpt)) {
      setOutdated(true)
      return
    }
    flashSection(finding.sectionId, { excerpt: finding.excerpt ?? null })
  }

  const handleFix = () => {
    if (!finding.sectionId) return
    if (finding.excerpt && !excerptMatches(finding.sectionId, finding.excerpt)) {
      setOutdated(true)
      return
    }
    onFix?.(finding)
  }

  const handleDismiss = () => {
    void dismissFinding(artifactId, finding.issueKey)
  }

  const statusChip = (() => {
    switch (finding.actionStatus) {
      case 'addressing':
        return <Badge variant="outline" className="h-5 text-[10px] gap-1 text-[hsl(var(--primary))]">Fixing…</Badge>
      case 'applied':
        // Covers both an AI-applied fix and a manual "Mark as fixed".
        return <Badge variant="success" className="h-5 text-[10px]">Fixed</Badge>
      case 'failed':
        return <Badge variant="destructive" className="h-5 text-[10px]">Fix failed</Badge>
      case 'commented':
        // Promoted to a comment thread — a person owns the fix now.
        return <Badge variant="outline" className="h-5 text-[10px]">Commented</Badge>
      default:
        return null
    }
  })()

  const unanchored = !finding.sectionId

  return (
    <div className="group relative rounded-xl border bg-card shadow-sm p-3 space-y-2">
      <div className="flex items-center gap-2 pr-6">
        <span className="text-meta inline-flex min-w-0 items-center gap-1.5">
          <span aria-hidden className={cn('h-1.5 w-1.5 shrink-0 rounded-full', severityDot)} />
          <span className="truncate">
            {severityLabel} · {finding.criterionName}
          </span>
        </span>
        {statusChip}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-1.5 right-1.5 h-6 w-6 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                onClick={handleDismiss}
                aria-label="Dismiss finding"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Dismiss finding</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <p
        ref={messageRef}
        className={cn('text-xs leading-relaxed', !expanded && 'line-clamp-2')}
      >
        {finding.message}
      </p>

      {finding.excerpt && (
        <blockquote
          ref={excerptRef}
          className={cn(
            'border-l-2 pl-2 text-[11px] italic text-muted-foreground',
            !expanded && 'line-clamp-3',
          )}
        >
          {finding.excerpt}
        </blockquote>
      )}

      {hasOverflow && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3 w-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" />
              Show more
            </>
          )}
        </button>
      )}

      {outdated ? (
        <div className="rounded-md bg-muted/60 border border-dashed p-2 space-y-1.5">
          <div className="flex items-center gap-1.5 text-meta">
            <AlertTriangle className="h-3.5 w-3.5" />
            The text this finding referred to has changed.
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => { setOutdated(false); handleJump() }}
            >
              Jump anyway
            </Button>
            {dismissFinding && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => { void dismissFinding(artifactId, finding.issueKey) }}
              >
                Dismiss finding
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1 pt-1">
          {canFix && !unanchored && finding.actionStatus !== 'applied' && (
            // Not disabled while `addressing`. That status is set the moment the
            // fix thread is created, not when a fix actually runs — and the user
            // still has to send the seeded instruction. Disabling on it stranded
            // anyone who clicked and didn't send: the finding stayed on "Fixing…"
            // through a reload with no way back in. The endpoint is built for the
            // second click ("Reuse existing thread when possible ... never
            // submitted"), so re-opening the same thread is the supported path.
            <Button
              variant="ai-ghost"
              size="sm"
              className="h-7 px-2 text-xs gap-1 whitespace-nowrap"
              onClick={handleFix}
            >
              <Wand2 className="h-3 w-3" />
              {finding.actionStatus === 'addressing' ? 'Resume fix' : 'Fix with AI'}
            </Button>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs gap-1 whitespace-nowrap"
                  onClick={handleJump}
                  disabled={unanchored}
                >
                  <ArrowRight className="h-3 w-3" />
                  Go to section
                </Button>
              </TooltipTrigger>
              {unanchored && (
                <TooltipContent>Jump/fix unavailable for general findings</TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          {finding.actionStatus === 'applied' ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs gap-1 whitespace-nowrap"
              onClick={() => { void markFindingFixed(artifactId, finding.issueKey, false) }}
            >
              <Undo2 className="h-3 w-3" />
              Reopen
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs gap-1 whitespace-nowrap"
              onClick={() => { void markFindingFixed(artifactId, finding.issueKey, true) }}
            >
              <Check className="h-3 w-3" />
              Mark fixed
            </Button>
          )}
          {canPromoteToComment && !unanchored && !alreadyPromoted && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label="More finding actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onPromoteToComment?.(finding)}>
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Add as comment
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {alreadyPromoted && (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Check className="h-3 w-3" />
              Added to comments
            </span>
          )}
        </div>
      )}
    </div>
  )
}
